import React, { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { useTranslation, Trans } from 'react-i18next';
import BreadCrumb from 'jbc-front/components/BreadCrumb';
import ActionButton from 'jbc-front/components/ActionButton';
import { Edit, Strage, MinusCircle, Download } from 'jbc-front/components/icons';
import { FORM_NAME_TAX_DEDUCTION_CERT_SEARCH } from 'src/reducers/searchForm';
import PaymentYearCalendarWithOfficeSelectBoxes from 'src/components/PaymentYearCalendarWithOfficeSelectBoxes';
import { YEAR_END_REPORT_INDEX } from 'src/constants/EndpointUrls';
import SearchFormPaginator from 'src/components/SearchFormPaginator';
import { REDUCER_NAME } from 'src/reducers/year_end_reports/yearEndReportTaxDeductionCerts';
import TaxDeductionCertsTableContainer from './containers/TaxDeductionCertsTableContainer';
import ResetConfirmationModal from './components/ResetConfirmationModal';
import styles from './ShowTaxDeductionCertsDataPage.scss';

const ShowTaxDeductionCertsDataPage = props => {
  const {
    isListLoading,
    formValues,
    employeePays,
    fetchGroupedPaymentYears,
    fetchYearEndReportTaxDeductionCerts,
    downloadTaxDeductionCerts,
    bulkUpdateAdjustments,
    isEditFormPristine,
    editFormInitialValues,
    fetchUnconfirmedMonths,
    resetAdjustment,
    changePage
  } = props;

  const { t } = useTranslation();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [resettingEmployee, setResettingEmployee] = useState(null);
  const resetFormRef = useRef(null);
  const submitFormRef = useRef(null);
  const isDisabledButton = isSaving || isEditFormPristine;
  const isDownloadDisabled = isSaving || employeePays.length === 0 || _.isUndefined(formValues?.groupedPaymentYears) || _.isUndefined(formValues?.offices);

  const handleDownloadCsv = () => {
    downloadTaxDeductionCerts(formValues);
  };

  const handleCancel = () => {
    if (resetFormRef.current) resetFormRef.current();
    setIsEditMode(false);
  };

  const handleReset = employeeId => {
    const employee = employeePays.find(e => e.employeeId === employeeId);
    if (employee) {
      setResettingEmployee(employee);
    }
  };

  const handleConfirmReset = async () => {
    if (!resettingEmployee) return;

    const year = _.get(formValues, 'groupedPaymentYears');
    try {
      setIsSaving(true);
      await resetAdjustment(year, resettingEmployee.employeeId, resettingEmployee.employeeName);
      setResettingEmployee(null);
      fetchYearEndReportTaxDeductionCerts(formValues, true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseModal = () => {
    setResettingEmployee(null);
  };

  const handleSave = async submittedValues => {
    const year = _.get(formValues, 'groupedPaymentYears');

    const fieldMap = {
      totalSsoAmount: 'sso_amount',
      totalCharityAmount: 'charity_amount',
      totalAssessableIncome: 'taxable_income',
      totalTaxableIncome: 'tax_calc_income',
      totalWithholdingTaxAmount: 'withheld_tax'
    };

    const adjustments = Object.entries(submittedValues)
      .map(([key, fields]) => {
        const initialFields = editFormInitialValues[key] || {};
        const isDirty = Object.entries(fields).some(([field, value]) => {
          const parseVal = val => (val === '' || val === null || val === undefined ? 0 : parseFloat(val));
          return parseVal(value) !== parseVal(initialFields[field]);
        });

        if (!isDirty) return null;

        const payload = { employee_id: key.replace(/^emp_/, '') };
        Object.entries(fields).forEach(([field, value]) => {
          if (fieldMap[field]) {
            const parsed = value === '' || value === null || value === undefined ? 0 : parseFloat(value);
            payload[fieldMap[field]] = Number.isNaN(parsed) ? null : parsed;
          }
        });
        return payload;
      })
      .filter(Boolean);

    if (adjustments.length === 0) {
      if (resetFormRef.current) resetFormRef.current();
      setIsEditMode(false);
      return;
    }

    try {
      setIsSaving(true);
      await bulkUpdateAdjustments(year, adjustments);
      if (resetFormRef.current) resetFormRef.current();
      setIsEditMode(false);
      fetchYearEndReportTaxDeductionCerts(formValues, true);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    fetchGroupedPaymentYears();
  }, []);

  const prevOfficesRef = useRef(undefined);
  const prevYearRef = useRef(undefined);

  useEffect(
    () => {
      if (_.isUndefined(formValues)) {
        return;
      }

      if (_.isUndefined(formValues.offices) || _.isUndefined(formValues.groupedPaymentYears)) {
        return;
      }

      const officesChanged = formValues.offices !== prevOfficesRef.current;
      const yearChanged = formValues.groupedPaymentYears !== prevYearRef.current;

      if (officesChanged || yearChanged) {
        prevOfficesRef.current = formValues.offices;
        prevYearRef.current = formValues.groupedPaymentYears;

        if (formValues.page !== 1) {
          changePage(1);
          return;
        }
      }

      fetchYearEndReportTaxDeductionCerts(formValues, true);
    },
    [formValues]
  );

  useEffect(
    () => {
      if (employeePays && employeePays.length > 0 && formValues?.groupedPaymentYears && formValues?.offices) {
        const employeeIds = employeePays.map(emp => emp.employeeId);
        fetchUnconfirmedMonths(formValues.groupedPaymentYears, formValues.offices, employeeIds);
      }
    },
    [employeePays]
  );

  return (
    <div className="l-wrap-xl l-contents-wrap">
      <BreadCrumb>
        <BreadCrumb.Item label={t('features.YearEndReportTaxDeductionCerts.ShowTaxDeductionCertsListPage.year_end_document')} path={YEAR_END_REPORT_INDEX} />
        <BreadCrumb.Item label={t('features.YearEndReportTaxDeductionCerts.ShowTaxDeductionCertsListPage.breadcrumb')} />
      </BreadCrumb>
      <div className="l-title-wrap">
        <h1 className="m-title-main">{t('features.YearEndReportTaxDeductionCerts.ShowTaxDeductionCertsListPage.header_title')}</h1>
      </div>
      <div className={styles.head}>
        <div className={styles.selectBox}>
          <PaymentYearCalendarWithOfficeSelectBoxes
            formName={FORM_NAME_TAX_DEDUCTION_CERT_SEARCH}
            currentYear={_.get(formValues, 'groupedPaymentYears')}
            onlyConfirmed
          />
        </div>
        <div className={styles.buttonBox}>
          {isEditMode ? (
            <>
              <ActionButton onClick={handleCancel} disabled={isSaving} className={`${styles.btn} ${styles.btnSecondary}`} icon={<MinusCircle size={15} />}>
                {t('features.YearEndReportTaxDeductionCerts.ShowTaxDeductionCertsListPage.cancel')}
              </ActionButton>
              <ActionButton
                onClick={() => submitFormRef.current && submitFormRef.current()}
                disabled={isDisabledButton}
                icon={<Strage size={15} />}
                primary
                className={styles.btn}
              >
                {isSaving
                  ? t('features.YearEndReportTaxDeductionCerts.ShowTaxDeductionCertsListPage.saving')
                  : t('features.YearEndReportTaxDeductionCerts.ShowTaxDeductionCertsListPage.save')}
              </ActionButton>
            </>
          ) : (
            <>
              <ActionButton onClick={() => setIsEditMode(true)} icon={<Edit size={15} />} className={`${styles.btn} ${styles.btnPrimary}`}>
                {t('features.YearEndReportTaxDeductionCerts.ShowTaxDeductionCertsListPage.edit_mode')}
              </ActionButton>
              <ActionButton
                onClick={handleDownloadCsv}
                disabled={isDownloadDisabled}
                icon={<Download size={15} />}
                className={`${styles.btn} ${styles.btnPrimary} ${styles.btnGap16}`}
              >
                {t('features.YearEndReportTaxDeductionCerts.ShowTaxDeductionCertsListPage.download_csv')}
              </ActionButton>
            </>
          )}
        </div>
      </div>
      <TaxDeductionCertsTableContainer
        isListLoading={isListLoading}
        isEditMode={isEditMode}
        employeePays={employeePays}
        onSave={handleSave}
        onReset={handleReset}
        onSetResetForm={fn => {
          resetFormRef.current = fn;
        }}
        onSetHandleSubmit={fn => {
          submitFormRef.current = fn;
        }}
      />
      <SearchFormPaginator formName={FORM_NAME_TAX_DEDUCTION_CERT_SEARCH} reducerName={REDUCER_NAME} />

      <ResetConfirmationModal
        header={t('features.YearEndReportTaxDeductionCerts.ShowTaxDeductionCertsListPage.reset_modal.title')}
        isOpen={!!resettingEmployee}
        hideModal={handleCloseModal}
        body={
          <React.Fragment>
            <p>
              <Trans
                i18nKey="features.YearEndReportTaxDeductionCerts.ShowTaxDeductionCertsListPage.reset_modal.body_p1"
                values={{ employeeName: resettingEmployee?.employeeName }}
                components={{
                  bold: <strong style={{ fontWeight: 'bold' }} />
                }}
              />
            </p>
            <p className="u-mt20">{t('features.YearEndReportTaxDeductionCerts.ShowTaxDeductionCertsListPage.reset_modal.body_p2')}</p>
            <p className="u-mt20">{t('features.YearEndReportTaxDeductionCerts.ShowTaxDeductionCertsListPage.reset_modal.body_p3')}</p>
          </React.Fragment>
        }
        onClick={handleConfirmReset}
        cancelBtnText={t('features.YearEndReportTaxDeductionCerts.ShowTaxDeductionCertsListPage.reset_modal.cancel')}
        confirmBtnText={t('features.YearEndReportTaxDeductionCerts.ShowTaxDeductionCertsListPage.reset_modal.confirm')}
      />
    </div>
  );
};

export default ShowTaxDeductionCertsDataPage;
